import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { fixedSlots } from "@/lib/listIdentity";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { WinnersOnlyNotice } from "@/components/WinnersOnlyNotice";
import { ClosedLoopNotice } from "@/components/ClosedLoopNotice";
import { Card3D, TinyCard, TINY_GRID } from "@/components/Card3D";
import {
  ArrowRight, Sparkles, Gift, Store as StoreIcon, Layers, Shuffle, Bot, Swords, Tv,
  Coins, Dna, Package, Telescope, ShieldCheck, Flag, Wand2, Eye, Trophy, Mountain,
  Image as ImageIcon, Bitcoin, MessageSquare, Hand, RotateCw, Lock, User, ChevronsUp, Send, Star,
  Drama, Network, Gauge, Flame, TrendingUp, Zap, Hourglass, Users, Crown, Lightbulb, BookOpen,
  Pickaxe, Backpack, Hammer, HeartPulse, ShieldPlus, Shield, Snowflake, Droplet, Boxes, Dices,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const showcaseCards = [
  { id: "showcase-1", serial: "GD-9X#KQF$M2-A", title: "Aurora Sovereign", rarity: "goddess", front_image: "https://images.unsplash.com/photo-1618367588421-400296bac364?auto=format&fit=crop&w=700&q=80", locked: false },
];

function fmtCompact(n) {
  if (n == null) return "—";
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, "") + "K";
  return n.toLocaleString();
}

const RARITY_BAR = [
  { key: "common", color: "#9ca3af" },
  { key: "uncommon", color: "#34d399" },
  { key: "rare", color: "#22d3ee" },
  { key: "epic", color: "#a78bfa" },
  { key: "legendary", color: "#fbbf24" },
  { key: "goddess", color: "#f472b6" },
];
const RARITY_COLORS = Object.fromEntries(RARITY_BAR.map((r) => [r.key, r.color]));

const TRAITS = [
  "Down Bad", "Obsessed Era", "Psycho Ex", "Gaslighter", "Comeback Kid", "Heartbreaker",
  "Touch Starved", "Femme Fatale", "Yandere Core", "Soft Launch", "Main Character", "Red Flag",
  "Green Flag", "Delulu", "Rizz Lord", "Cold Shoulder", "Glow Up", "Villain Origin",
  "Ride or Die", "Toxic Loyal", "Sigma Grind", "Slow Burn", "Hopeless Romantic", "Left On Read",
];

function useHomeStats() {
  return useQuery({ queryKey: ["home-stats"], queryFn: async () => (await api.get("/home/stats")).data });
}

/* Scroll-reveal wrapper */
function Reveal({ children, delay = 0, className = "", y = 26 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function SectionHeading({ eyebrow, title, accent = "#22d3ee", children, cta }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div className="min-w-0 max-w-full">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em]" style={{ color: accent }}>{eyebrow}</div>
        <h2 className="font-display text-[26px] xs:text-3xl sm:text-4xl md:text-5xl text-white tracking-[0.02em] sm:tracking-[0.06em] mt-2 break-words leading-[1.05]">{title}</h2>
        {children && <p className="text-white/55 mt-3 max-w-2xl text-sm leading-relaxed">{children}</p>}
      </div>
      {cta}
    </div>
  );
}

/* ── HERO ─────────────────────────────────────────────────────────── */
// Up to 3 admin-selected cards, fanned out. The center card is fully interactive
// (drag-spin / flip); the flanking cards sit angled behind it for depth.
function HeroCardFan({ cards }) {
  const list = (cards || []).filter(Boolean).slice(0, 3);
  const [frontId, setFrontId] = useState(null);
  if (list.length <= 1) {
    return <Card3D card={list[0] || showcaseCards[0]} size="lg" interactive alwaysOwner />;
  }
  // Put the chosen "front" card in the middle slot; the rest flank it.
  const front = list.find((c) => c.id === frontId) || list[Math.floor(list.length / 2)];
  const others = list.filter((c) => c !== front);
  const arranged = front ? [others[0], front, others[1]].filter(Boolean) : list;
  const center = arranged.indexOf(front);
  return (
    <div className="relative mx-auto" style={{ width: 300, height: 420 }} data-testid="hero-card-fan">
      {arranged.map((c, i) => {
        const pos = i - center;
        const isCenter = pos === 0;
        return (
          <div
            key={c.id || i}
            data-testid={`hero-card-${isCenter ? "front" : "back"}`}
            onClick={isCenter ? undefined : () => setFrontId(c.id)}
            title={isCenter ? undefined : "Bring to front"}
            className={`absolute left-1/2 top-1/2 transition-transform duration-500 ${isCenter ? "" : "cursor-pointer"}`}
            style={{
              transform: `translate(-50%,-50%) translateX(${pos * 96}px) translateY(${Math.abs(pos) * 18}px) rotate(${pos * 7}deg) scale(${isCenter ? 1 : 0.84})`,
              zIndex: isCenter ? 30 : 12 - Math.abs(pos),
            }}
          >
            <Card3D card={c} size="md" interactive={isCenter} alwaysOwner hoverStats previewSide={pos < 0 ? "right" : "left"} />
          </div>
        );
      })}
    </div>
  );
}

/* ── LIVE NOW STRIP — real, right-now activity (not all-time totals) ──── */
function LiveNowStrip() {
  const { data } = useQuery({
    queryKey: ["live-activity"],
    queryFn: async () => (await api.get("/home/live-activity")).data,
    refetchInterval: 12000,
  });
  if (!data) return null;
  const chips = [
    ...data.spectator_matches.map((m) => ({
      key: `m-${m.id}`, label: `${m.a} vs ${m.b}`, sub: m.status === "betting" ? "Predictions open" : "Live now",
      href: `/arena/watch/${m.id}`, Icon: Swords, color: "#fb7185",
    })),
    ...data.races.map((r) => ({
      key: `r-${r.id}`, label: `${r.lanes}-lane ${r.spectator ? "Gauntlet" : "Race"}`,
      sub: r.status === "betting" ? "Predictions open" : "Racing now",
      href: r.spectator ? `/race/watch/${r.id}` : `/race/${r.id}`, Icon: Flag, color: "#4ade80",
    })),
    ...data.shows.map((s) => ({
      key: `s-${s.id}`, label: s.name, sub: "On stage now",
      href: `/talentshow/${s.id}`, Icon: Drama, color: "#f472b6",
    })),
  ].slice(0, 4);
  const hasAny = chips.length > 0 || data.arena_live > 0 || data.cavern_live > 0 || data.races_live > 0;
  if (!hasAny) return null;
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="live-now-strip">
      <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-emerald-300/90 shrink-0">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_#34d399]" /> Live Now
      </span>
      {chips.map(({ key, label, sub, href, Icon, color }) => (
        <Link key={key} to={href} data-testid={`live-now-chip-${key}`}
          className="group inline-flex items-center gap-2 hud-pill rounded-full pl-2 pr-3 py-1 hover:border-white/25 transition-colors">
          <Icon className="h-3 w-3" style={{ color }} />
          <span className="font-mono text-[10px] text-white/80 truncate max-w-[9rem]">{label}</span>
          <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/35 group-hover:text-white/60">{sub}</span>
        </Link>
      ))}
      {data.arena_live > 0 && (
        <span data-testid="live-now-arena-count" className="inline-flex items-center gap-1.5 font-mono text-[10px] text-white/50">
          <Swords className="h-3 w-3 text-amber-300" /> {data.arena_live} arena battle{data.arena_live === 1 ? "" : "s"} raging
        </span>
      )}
      {data.races_live > 0 && (
        <span data-testid="live-now-races-count" className="inline-flex items-center gap-1.5 font-mono text-[10px] text-white/50">
          <Flag className="h-3 w-3 text-emerald-300" /> {data.races_live} race{data.races_live === 1 ? "" : "s"} on the track
        </span>
      )}
      {data.cavern_live > 0 && (
        <span data-testid="live-now-cavern-count" className="inline-flex items-center gap-1.5 font-mono text-[10px] text-white/50">
          <Mountain className="h-3 w-3 text-fuchsia-300" /> {data.cavern_live} part{data.cavern_live === 1 ? "y" : "ies"} delving
        </span>
      )}
    </div>
  );
}

function Hero() {
  const { user } = useAuth();
  const { data: featured } = useQuery({ queryKey: ["home-featured"], queryFn: async () => (await api.get("/home/featured")).data });
  const { data: stats } = useHomeStats();
  const heroCards = (Array.isArray(featured) && featured.length) ? featured.slice(0, 3) : [showcaseCards[0]];
  const act = stats?.activity || {};
  const livePills = [
    { label: "Battles fought", value: fmtCompact(act.battles_fought ?? 0), color: "#fb7185" },
    { label: "Gauntlets run", value: fmtCompact(act.races_run ?? 0), color: "#4ade80" },
    { label: "Cavern delves", value: fmtCompact(act.cavern_delves ?? 0), color: "#e879f9" },
  ];
  const heroStats = [
    { label: "Cards to Discover", value: stats ? fmtCompact(stats.undiscovered) + "+" : "…" },
    { label: "Card Designs", value: stats ? fmtCompact(stats.card_designs) : "…" },
    { label: "Find-on-Mint Traits", value: stats ? fmtCompact(stats.traits) + "+" : "1.8K+" },
  ];
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 gridline opacity-30 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[40rem] h-[40rem] rounded-full bg-cyan-500/10 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[34rem] h-[34rem] rounded-full bg-fuchsia-500/10 blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <div className="inline-flex items-center gap-2 hud-pill px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-300">
              <Sparkles className="h-3 w-3" /> 21+ · Crypto-Native · Closed-Loop Entertainment Platform
            </div>
            <Link to="/help" data-testid="hero-help-link" className="group inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.3em] text-amber-200 hover:bg-amber-400/20 hover:border-amber-400/70 transition-colors">
              <BookOpen className="h-3 w-3" /> How to Play <ArrowRight className="h-3 w-3 -ml-0.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-[0.04em] leading-[0.92]">
            ENTER THE<br />
            <span className="bg-gradient-to-r from-cyan-300 via-emerald-300 to-amber-300 bg-clip-text text-transparent">PLAYDEN.</span>
          </h1>
          <p className="text-white/60 mt-5 max-w-xl text-base leading-relaxed">
            A living world of holographic, serialized cards you <span className="text-cyan-300">collect, trade, battle, race, raid and reforge</span>. Every card reveals its art to you alone, mints with its own original name &amp; lore, rolls from <span className="text-emerald-300">{stats ? fmtCompact(stats.traits) + "+" : "1,800+"} Find-on-Mint traits</span>, then levels up and evolves as it fights. Top up with <span className="text-amber-300">crypto</span> and play for keeps.
          </p>
          <ClosedLoopNotice variant="compact" withBusinessModel className="mt-5 max-w-xl" testid="home-closed-loop" />
          <WinnersOnlyNotice className="mt-6 max-w-xl" />
          <div className="mt-7 flex flex-wrap gap-3">
            {user ? (
              <>
                <Button asChild data-testid="hero-collection-button" size="lg" className="bg-cyan-400 text-black hover:bg-cyan-300 shadow-[0_18px_50px_rgba(0,220,255,0.25)]">
                  <Link to="/collection"><Layers className="mr-2 h-4 w-4" /> My Playden</Link>
                </Button>
                <Button asChild data-testid="hero-arena-button" size="lg" className="bg-amber-400 text-black hover:bg-amber-300 shadow-[0_18px_50px_rgba(255,190,0,0.22)]">
                  <Link to="/arena"><Swords className="mr-2 h-4 w-4" /> Enter Arena</Link>
                </Button>
                <Button asChild data-testid="hero-cavern-button" variant="outline" size="lg" className="border-fuchsia-400/40 text-fuchsia-200 hover:bg-fuchsia-400/10">
                  <Link to="/cavern"><Mountain className="mr-2 h-4 w-4" /> Raid the Cavern</Link>
                </Button>
              </>
            ) : (
              <>
                <Button asChild data-testid="hero-register-button" size="lg" className="bg-cyan-400 text-black hover:bg-cyan-300 shadow-[0_18px_50px_rgba(0,220,255,0.25)]">
                  <Link to="/register">Start Collecting <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild data-testid="hero-store-button" size="lg" className="bg-amber-400 text-black hover:bg-amber-300 shadow-[0_18px_50px_rgba(255,190,0,0.22)]">
                  <Link to="/store"><StoreIcon className="mr-2 h-4 w-4" /> Enter Store</Link>
                </Button>
                <Button asChild data-testid="hero-explore-button" variant="outline" size="lg" className="border-white/15 text-white hover:bg-white/5">
                  <Link to="/explore"><Telescope className="mr-2 h-4 w-4" /> Explore</Link>
                </Button>
              </>
            )}
          </div>

          <LiveNowStrip />

          <div className="mt-8 grid grid-cols-3 gap-2 sm:gap-3 max-w-md">
            {heroStats.map((s) => (
              <div key={s.label} className="hud-pill rounded-xl p-2.5 sm:p-3 min-w-0" data-testid={`hero-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="font-display text-xl sm:text-2xl text-white truncate">{s.value}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 mt-1 break-words">{s.label}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div className="relative flex flex-col items-center lg:items-end"
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
          <div className="absolute -inset-10 rounded-[50%] bg-gradient-to-t from-cyan-400/10 to-transparent blur-3xl pointer-events-none" />
          <div className="relative">
            <HeroCardFan cards={heroCards} />
            <div className="mt-4 flex items-center justify-center gap-4 font-mono text-[9px] uppercase tracking-[0.18em] text-white/45">
              <span className="inline-flex items-center gap-1"><Hand className="h-3 w-3 text-cyan-300" /> Drag to spin</span>
              <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3 text-emerald-300" /> Tap holo</span>
              <span className="inline-flex items-center gap-1"><RotateCw className="h-3 w-3 text-amber-300" /> Flip</span>
            </div>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {livePills.map((p) => (
                <div key={p.label} className="hud-pill rounded-full px-3 py-1 flex items-center gap-2" data-testid={`hero-live-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: p.color, boxShadow: `0 0 8px ${p.color}` }} />
                  <span className="font-display text-base text-white leading-none">{p.value}</span>
                  <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/40">{p.label}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
      <VitalsTicker />
    </section>
  );
}

/* ── LIVE VITALS MARQUEE ──────────────────────────────────────────── */
function VitalsTicker() {
  const { data: s } = useHomeStats();
  const a = s?.activity || {};
  const items = [
    { label: "Card Designs", value: fmtCompact(s?.card_designs ?? 0) },
    { label: "Editions Minted", value: fmtCompact(s?.minted ?? 0) },
    { label: "Undiscovered", value: fmtCompact(s?.undiscovered ?? 0) + "+" },
    { label: "Collectors", value: fmtCompact(s?.collectors ?? 0) },
    { label: "Battles Fought", value: fmtCompact(a.battles_fought ?? 0) },
    { label: "Gauntlets Run", value: fmtCompact(a.races_run ?? 0) },
    { label: "Cavern Delves", value: fmtCompact(a.cavern_delves ?? 0) },
    { label: "Talent Shows", value: fmtCompact(a.talent_shows ?? 0) },
    { label: "Cards Evolved", value: fmtCompact(a.cards_evolved ?? 0) },
    { label: "Trades Settled", value: fmtCompact(a.trades_done ?? 0) },
    { label: "Find-on-Mint Traits", value: fmtCompact(s?.traits ?? 0) + "+" },
    { label: "Rarity Tiers", value: s?.rarity_tiers ?? 6 },
  ];
  const row = ["first", "repeat"].flatMap((copy) => items.map((item) => ({ ...item, key: `${copy}:${item.label}` })));
  return (
    <div className="border-y border-white/5 bg-black/30 backdrop-blur-sm marquee-mask overflow-hidden py-3" data-testid="live-vitals-ticker">
      <div className="marquee-track gap-8">
        {row.map((it, i) => (
          <div key={it.key} className="shrink-0 inline-flex items-baseline gap-2 whitespace-nowrap">
            <span className="font-display text-xl text-white leading-none">{it.value}</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-cyan-300/70">{it.label}</span>
            <span className="text-white/15 ml-2">//</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── RARITY SPECTRUM ──────────────────────────────────────────────── */
function RaritySpectrum() {
  const tiers = [
    { rarity: "common", title: "Common", desc: "Matte, crisp" },
    { rarity: "uncommon", title: "Uncommon", desc: "Mint sheen" },
    { rarity: "rare", title: "Rare", desc: "Cyan holo sweep" },
    { rarity: "epic", title: "Epic", desc: "Violet holo glow" },
    { rarity: "legendary", title: "Legendary", desc: "Gold animated foil" },
    { rarity: "goddess", title: "Goddess", desc: "Iridescent + particles" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <Reveal>
        <div className="text-center mb-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80">Rarity Spectrum</div>
          <h2 className="font-display text-4xl sm:text-5xl text-white tracking-[0.06em] mt-2">SIX TIERS OF PRESTIGE.</h2>
        </div>
      </Reveal>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiers.map((t, i) => (
          <Reveal key={t.rarity} delay={i * 0.06}>
            <Link to="/explore" data-testid={`rarity-tier-${t.rarity}`} className={`rarity-${t.rarity} card-aura rounded-xl p-4 aspect-[2.5/3.5] bg-[hsl(220_18%_8%)] relative overflow-hidden block transition-transform duration-300 hover:-translate-y-2`}>
              <div className="foil-overlay" />
              {t.rarity === "goddess" && (
                <div className="sparkle-layer">
                  {fixedSlots(10).map(({ id, index: k }) => (
                    <span key={id} className="sparkle" style={{ top: `${(k * 37) % 100}%`, left: `${(k * 61) % 100}%`, animationDelay: `${(k % 4) / 2}s` }} />
                  ))}
                </div>
              )}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
                <div className="font-display text-lg tracking-[0.1em]" style={{ color: "var(--label)" }}>{t.title}</div>
                <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/40 mt-2">{t.desc}</div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ── TRAITS MARQUEE ───────────────────────────────────────────────── */
function TraitsMarquee() {
  const row = ["first", "repeat"].flatMap((copy) => TRAITS.map((text) => ({ key: `${copy}:${text}`, text })));
  return (
    <div className="marquee-mask marquee-paused overflow-hidden py-1" data-testid="traits-marquee">
      <div className="marquee-track gap-2.5">
        {row.map((t, i) => (
          <span key={t.key} className="shrink-0 rounded-full border border-emerald-300/25 bg-emerald-300/5 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.12em] text-emerald-200/80">
            {t.text}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── MINTING ENGINE (privacy, AI lore, traits) ───────────────────── */
function MintingEngine() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="home-minting-engine">
      <Reveal>
        <SectionHeading eyebrow="The Minting Engine" title="EVERY MINT IS ONE OF ONE." accent="#34d399">
          A card isn't yours until it's minted to your Playden — and every fresh mint rolls its own identity. Same design, different soul.
        </SectionHeading>
      </Reveal>
      <div className="grid md:grid-cols-12 gap-3">
        <Reveal className="md:col-span-6" delay={0.05}>
          <Link to="/collection" data-testid="bento-privacy-gating" className="hud-pill rounded-2xl p-6 h-full relative overflow-hidden group hover:border-cyan-400/30 transition-colors block">
            <div className="absolute inset-0 holo-scan opacity-15" />
            <Lock className="h-7 w-7 text-cyan-300" />
            <div className="font-display text-2xl text-white mt-3 tracking-[0.05em]">PRIVATE HOLO CARDS</div>
            <p className="text-sm text-white/55 mt-2 leading-relaxed">Own a card and its art unlocks <span className="text-white">for your eyes only</span>. Everyone else sees a locked, serialized silhouette — your gallery is genuinely yours.</p>
          </Link>
        </Reveal>
        <Reveal className="md:col-span-6" delay={0.12}>
          <Link to="/explore" data-testid="bento-named-lore" className="hud-pill rounded-2xl p-6 h-full relative overflow-hidden group hover:border-fuchsia-400/30 transition-colors block">
            <div className="absolute inset-0 holo-scan opacity-15" />
            <Wand2 className="h-7 w-7 text-fuchsia-300" />
            <div className="font-display text-2xl text-white mt-3 tracking-[0.05em]">NAMED &amp; GIVEN LORE</div>
            <p className="text-sm text-white/55 mt-2 leading-relaxed">Every card mints with its own <span className="text-white">original AI-written name and backstory</span> — a one-of-one identity no other copy will ever share.</p>
          </Link>
        </Reveal>
        <Reveal className="md:col-span-12 min-w-0" delay={0.18}>
          <div className="hud-pill rounded-2xl p-6 relative overflow-hidden min-w-0 max-w-full" data-testid="bento-traits">
            <div className="flex items-start gap-3 mb-4 min-w-0">
              <Dna className="h-7 w-7 text-emerald-300 shrink-0" />
              <div className="min-w-0">
                <div className="font-display text-2xl text-white tracking-[0.05em]">1,800+ FIND-ON-MINT TRAITS</div>
                <p className="text-sm text-white/55 mt-1 leading-relaxed">Rolled at mint, traits dictate how a card fights. Rarer cards find more — and the rarest unlock active moves like <span className="text-violet-200">Interrupt</span> that commons never could.</p>
              </div>
            </div>
            <TraitsMarquee />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ── GAME MODES BENTO (Arena · Cavern · Gauntlet · Talent Show) ───── */
function GameModesBento() {
  const { data: stats } = useHomeStats();
  const a = stats?.activity || {};
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="home-game-modes">
      <Reveal>
        <SectionHeading eyebrow="The Core Loop" title="FOUR WAYS TO PLAY FOR KEEPS." accent="#fb7185">
          Your cards are living soldiers, runners, raiders and performers. Every mode runs on their minted stats &amp; traits — with real card stakes on the line.
        </SectionHeading>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        {/* Arena — large */}
        <Reveal className="md:col-span-8" delay={0.04}>
          <Link to="/arena" data-testid="bento-card-arena" className="group relative block h-full rounded-2xl border border-amber-400/25 bg-[hsl(220_18%_8%)] overflow-hidden p-6 sm:p-8 transition-all duration-300 hover:border-amber-400/60 hover:-translate-y-1">
            <div className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-amber-500/10 blur-[90px] pointer-events-none" />
            <div className="absolute inset-0 holo-scan opacity-15" />
            <div className="relative">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300/80"><Swords className="h-3.5 w-3.5" /> Battle · {fmtCompact(a.battles_fought ?? 0)} fought</div>
              <h3 className="font-display text-4xl sm:text-5xl text-white tracking-[0.05em] mt-2">THE VS ARENA</h3>
              <p className="text-white/55 text-sm mt-2 max-w-xl leading-relaxed">Turn-based squad combat with crits, lifesteal, shields, afflictions, trait-granted Interrupts and last-ditch revives — assembled in the War Room squad picker.</p>
              <div className="grid sm:grid-cols-3 gap-2.5 mt-5">
                {[
                  { Icon: Bot, c: "#a78bfa", t: "vs The Machine", d: "Rarity-matched AI · pick difficulty · commit credits as a Challenge Entry." },
                  { Icon: Swords, c: "#22d3ee", t: "Challenge Players", d: "Stake credits + cards. Winner keeps the loser's soldiers." },
                  { Icon: Tv, c: "#fbbf24", t: "Community Prediction Pool", d: "Commit credits to a side during the prediction window, split the pari-mutuel pool." },
                ].map((m) => (
                  <div key={m.t} className="rounded-xl border border-white/10 bg-black/30 p-3">
                    <m.Icon className="h-5 w-5" style={{ color: m.c }} />
                    <div className="font-display text-base text-white mt-1.5 tracking-[0.04em]">{m.t}</div>
                    <div className="text-[11px] text-white/50 mt-0.5 leading-snug">{m.d}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-300 group-hover:gap-3 transition-all">Enter the Arena <ArrowRight className="h-3.5 w-3.5" /></div>
            </div>
          </Link>
        </Reveal>

        {/* Cavern — tall */}
        <Reveal className="md:col-span-4" delay={0.1}>
          <Link to="/cavern" data-testid="bento-card-cavern" className="group relative block h-full rounded-2xl border border-fuchsia-400/25 bg-[hsl(220_18%_8%)] overflow-hidden p-6 transition-all duration-300 hover:border-fuchsia-400/60 hover:-translate-y-1">
            <div className="absolute -bottom-20 -left-16 w-64 h-64 rounded-full bg-fuchsia-500/12 blur-[80px] pointer-events-none" />
            <div className="absolute inset-0 holo-scan opacity-15" />
            <div className="relative flex flex-col h-full">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-300/80"><Mountain className="h-3.5 w-3.5" /> Co-op Raid</div>
              <h3 className="font-display text-3xl text-white tracking-[0.05em] mt-2">THE CAVERN</h3>
              <p className="text-white/55 text-sm mt-2 leading-relaxed">An interactive turn-based boss raid. Party up, watch the SPD initiative bar, and time your strikes — <span className="text-emerald-200">Brace</span> or <span className="text-violet-200">Interrupt</span> the boss's telegraphed casts before it wipes the floor.</p>
              <div className="mt-auto pt-5 space-y-1.5">
                <div className="flex items-center gap-2 text-[11px] text-white/55"><Users className="h-3.5 w-3.5 text-fuchsia-300" /> Up to a 5-card party</div>
                <div className="flex items-center gap-2 text-[11px] text-white/55"><Hourglass className="h-3.5 w-3.5 text-amber-300" /> Live ATB turn order</div>
                <div data-testid="home-cavern-reward-rule" className="flex items-center gap-2 text-[11px] text-white/55"><Trophy className="h-3.5 w-3.5 text-emerald-300 shrink-0" /> Win → loot, 25% card chance · wipe → forfeit</div>
              </div>
              <div className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-fuchsia-300 group-hover:gap-3 transition-all">Descend <ArrowRight className="h-3.5 w-3.5" /></div>
            </div>
          </Link>
        </Reveal>

        {/* Gauntlet */}
        <Reveal className="md:col-span-4" delay={0.16}>
          <Link to="/race" data-testid="bento-card-gauntlet" className="group relative block h-full rounded-2xl border border-emerald-400/25 bg-[hsl(220_18%_8%)] overflow-hidden p-6 transition-all duration-300 hover:border-emerald-400/60 hover:-translate-y-1">
            <div className="absolute -top-16 -right-12 w-56 h-56 rounded-full bg-emerald-500/10 blur-[80px] pointer-events-none" />
            <div className="absolute inset-0 holo-scan opacity-15" />
            <div className="relative">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300/80"><Flag className="h-3.5 w-3.5" /> Race · {fmtCompact(a.races_run ?? 0)} run</div>
              <h3 className="font-display text-3xl text-white tracking-[0.05em] mt-2">THE GAUNTLET</h3>
              <p data-testid="home-race-reward-rule" className="text-white/55 text-sm mt-2 leading-relaxed">Competitive first place earns gear or a consumable, never new cards or credits. Human buy-ins fund the pot; AI contributes nothing. Last place still forfeits a runner, with 15-minute owner priority. Shared-code cards stay owned but permanently locked out.</p>
              <div className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-300 group-hover:gap-3 transition-all">Run the Gauntlet <ArrowRight className="h-3.5 w-3.5" /></div>
            </div>
          </Link>
        </Reveal>

        {/* Talent Show — wide */}
        <Reveal className="md:col-span-8" delay={0.22}>
          <Link to="/talentshow" data-testid="bento-card-talentshow" className="group relative block h-full rounded-2xl border border-orange-400/25 bg-[hsl(220_18%_8%)] overflow-hidden p-6 sm:p-7 transition-all duration-300 hover:border-orange-400/60 hover:-translate-y-1">
            <div className="absolute -bottom-20 -right-16 w-72 h-72 rounded-full bg-orange-500/10 blur-[90px] pointer-events-none" />
            <div className="absolute inset-0 holo-scan opacity-15" />
            <div className="relative flex flex-wrap items-center justify-between gap-4">
              <div className="max-w-lg">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-orange-300/80"><Drama className="h-3.5 w-3.5" /> Parade · {fmtCompact(a.talent_shows ?? 0)} held</div>
                <h3 className="font-display text-3xl sm:text-4xl text-white tracking-[0.05em] mt-2">THE TALENT SHOW</h3>
                <p className="text-white/55 text-sm mt-2 leading-relaxed">Put your cards on the catwalk in a multiplayer parade. Start your own show with custom rules, send performers down the runway, show off a stat to extend the spotlight, and let the crowd hype meter crown the star.</p>
              </div>
              <div className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-orange-300 group-hover:gap-3 transition-all">Take the stage <ArrowRight className="h-3.5 w-3.5" /></div>
            </div>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ── THE PATH (Branching skill tree · Quests · Commissions) ───────── */
function PathPrestige() {
  const lanes = [
    { Icon: Swords, c: "#fb7185", t: "Warrior" },
    { Icon: Drama, c: "#fb923c", t: "Showrunner" },
    { Icon: Layers, c: "#a78bfa", t: "Collector" },
    { Icon: Flag, c: "#4ade80", t: "Racer" },
    { Icon: Wand2, c: "#22d3ee", t: "Artificer" },
    { Icon: Coins, c: "#fbbf24", t: "Merchant" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="home-path">
      <Reveal>
        <SectionHeading eyebrow="Progress" title="WALK THE PATH." accent="#fde047"
          cta={<Button asChild data-testid="path-showcase-cta" className="bg-yellow-300 text-black hover:bg-yellow-200"><Link to="/path"><Trophy className="mr-2 h-4 w-4" /> Open the Path</Link></Button>}>
          Earn Skill Points from everything you do, then carve your own route through six procedurally-generated skill trees. Every node you collect drops loot — never the same map twice.
        </SectionHeading>
      </Reveal>
      <div className="grid lg:grid-cols-3 gap-3">
        <Reveal className="lg:col-span-2" delay={0.05}>
          <div className="hud-pill rounded-2xl p-6 h-full relative overflow-hidden">
            <div className="absolute inset-0 holo-scan opacity-10" />
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-300/80"><Network className="h-3.5 w-3.5" /> 6 Trees · Branch Outward</div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5 mt-4">
              {lanes.map((l) => (
                <Link key={l.t} to="/path" data-testid={`path-lane-${l.t.toLowerCase()}`} className="rounded-xl border border-white/10 bg-black/30 p-3 flex flex-col items-center text-center hover:-translate-y-1 transition-transform" style={{ borderColor: `${l.c}33` }}>
                  <l.Icon className="h-5 w-5" style={{ color: l.c }} />
                  <div className="font-display text-sm text-white mt-1.5 tracking-[0.04em]">{l.t}</div>
                  <div className="mt-2 grid grid-cols-3 gap-0.5">
                    {fixedSlots(9).map(({ id, index: k }) => <span key={id} className="h-1 w-1 rounded-full" style={{ background: k < 3 ? l.c : "rgba(255,255,255,0.15)" }} />)}
                  </div>
                </Link>
              ))}
            </div>
            <p className="text-[11px] text-white/45 mt-4 leading-relaxed">Each tree is a <span className="text-yellow-200">3-wide × 10-deep lattice</span>. Start at the top and branch <span className="text-yellow-200">left or right</span> — every cell you unlock opens the nodes below it and pays out a random reward: <span className="text-emerald-200">gear</span>, <span className="text-cyan-200">cards</span>, <span className="text-fuchsia-200">packs</span> or permanent <span className="text-emerald-200">buffs</span>. Deeper nodes cost more SP and roll rarer loot — all the way to Goddess.</p>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="space-y-3 h-full">
            <Link to="/path" data-testid="path-quests-card" className="hud-pill rounded-2xl p-5 block relative overflow-hidden group hover:border-emerald-400/30 transition-colors">
              <Sparkles className="h-6 w-6 text-emerald-300" />
              <div className="font-display text-xl text-white mt-2 tracking-[0.04em]">DAILY &amp; WEEKLY QUESTS</div>
              <p className="text-[12px] text-white/55 mt-1 leading-relaxed">Rolling objectives that pay Skill Points, credits and packs. Claim, repeat, climb.</p>
            </Link>
            <Link to="/path" data-testid="path-commissions-card" className="hud-pill rounded-2xl p-5 block relative overflow-hidden group hover:border-amber-400/30 transition-colors">
              <Hourglass className="h-6 w-6 text-amber-300" />
              <div className="font-display text-xl text-white mt-2 tracking-[0.04em]">COMMISSION BOARD</div>
              <p className="text-[12px] text-white/55 mt-1 leading-relaxed">Buy a timed quest with Gold, SP or staked cards — beat the clock for packs, SP or fresh cards. Miss it and the stake is forfeit.</p>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ── LEVELING & EVOLUTION ─────────────────────────────────────────── */
function LevelingEvolution() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="home-leveling">
      <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 items-center">
        <Reveal>
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80">Living Cards</div>
          <h2 className="font-display text-4xl sm:text-5xl text-white tracking-[0.06em] mt-2 leading-tight">THEY FIGHT.<br />THEY GROW.<br /><span className="bg-gradient-to-r from-cyan-300 to-fuchsia-300 bg-clip-text text-transparent">THEY EVOLVE.</span></h2>
          <p className="text-white/55 mt-4 text-sm leading-relaxed max-w-lg">Every battle, race and raid feeds your cards XP. They climb from <span className="text-white">Level 1 to Level 10</span>, gaining a permanent stat buff each level — then <span className="text-fuchsia-200">evolve a full rarity tier higher</span>, art and all. Evolution sticks, even through forfeits.</p>
          <div className="mt-6 flex flex-wrap gap-2.5">
            <Button asChild data-testid="leveling-arena-cta" className="bg-cyan-400 text-black hover:bg-cyan-300"><Link to="/arena"><Swords className="mr-2 h-4 w-4" /> Battle for XP</Link></Button>
            <Button asChild data-testid="leveling-collection-cta" variant="outline" className="border-white/15 text-white hover:bg-white/5"><Link to="/collection"><Layers className="mr-2 h-4 w-4" /> See your roster</Link></Button>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="hud-pill rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute inset-0 holo-scan opacity-15" />
            <div className="relative space-y-4">
              <div>
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-white/45 mb-2"><span className="inline-flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-cyan-300" /> XP · Level 7 / 10</span><span className="text-cyan-300">+30% stats</span></div>
                <div className="h-2 rounded-full bg-black/60 border border-white/10 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400" style={{ width: "70%" }} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[
                  { Icon: Zap, t: "ATK", v: "+34" },
                  { Icon: ShieldCheck, t: "DEF", v: "+21" },
                  { Icon: Gauge, t: "SPD", v: "+18" },
                ].map((s) => (
                  <div key={s.t} className="rounded-xl border border-white/10 bg-black/30 p-3">
                    <s.Icon className="h-4 w-4 mx-auto text-emerald-300" />
                    <div className="font-display text-lg text-white mt-1">{s.v}</div>
                    <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/40">{s.t}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-400/[0.06] p-3 flex items-center gap-3">
                <Flame className="h-6 w-6 text-fuchsia-300 shrink-0" />
                <div>
                  <div className="font-display text-base text-white tracking-[0.04em]">EVOLUTION AT L10</div>
                  <div className="text-[11px] text-white/55 leading-snug">Rare → Epic → Legendary → Goddess. A whole new card, kept forever.</div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ── ADVENTURE + COMBAT ARSENAL ──────────────────────────────────── */
const ADVENTURE_STEPS = [
  { Icon: Pickaxe, c: "#84cc16", t: "Gather", d: "Send a card to its profession node to mine ore, pick herbs and pull rare essence — powered by Stamina, not credits." },
  { Icon: Hammer, c: "#f59e0b", t: "Craft", d: "Blacksmiths, Alchemists & Enchanters forge tiered materials into equippable gear — each piece rolls a unique name and stats." },
  { Icon: Backpack, c: "#22d3ee", t: "Equip", d: "Slot Weapon, Armor, Trinket & Relic onto any card. Gear auto-applies in the Arena, Cavern and Gauntlet — build once, fight buffed everywhere." },
  { Icon: Boxes, c: "#a78bfa", t: "Trade Gear", d: "List spare gear on the Player Gear Market or crack open admin Gear Crates — your loot now has a real economy." },
];
const MOVE_KINDS = [
  { Icon: Swords, c: "#fda4af", t: "Strike", d: "Elemental damage — 121 attacks across 8 elements." },
  { Icon: HeartPulse, c: "#34d399", t: "Heal", d: "Mend a chosen ally — and fully cleanse their poison/DoT." },
  { Icon: ShieldPlus, c: "#38bdf8", t: "Defend", d: "Raise a shield that soaks damage before it hits HP." },
  { Icon: Hourglass, c: "#a78bfa", t: "Disrupt", d: "Set back the foe's attack timers and slow their tempo." },
];
function ArsenalAndAdventure() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="home-arsenal">
      <Reveal>
        <SectionHeading eyebrow="Make It · Master It" title="GATHER. CRAFT. THEN GO TO WAR." accent="#84cc16"
          cta={<Button asChild data-testid="arsenal-adventure-cta" className="bg-lime-400 text-black hover:bg-lime-300"><Link to="/adventure"><Pickaxe className="mr-2 h-4 w-4" /> Open Adventure</Link></Button>}>
          Every card has a profession. Put them to work, forge gear no one else has, then take a 225-strong move arsenal into battle — including a brand-new wave of heal, shield and disrupt tech.
        </SectionHeading>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-3">
        <Reveal delay={0.05}>
          <div className="hud-pill rounded-2xl p-6 h-full relative overflow-hidden">
            <div className="absolute inset-0 holo-scan opacity-10" />
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-lime-300/80"><Pickaxe className="h-3.5 w-3.5" /> The Adventure Loop</div>
            <div className="mt-4 space-y-2.5">
              {ADVENTURE_STEPS.map((s, i) => (
                <Link key={s.t} to="/adventure" data-testid={`arsenal-step-${s.t.toLowerCase().replace(/\s/g, "-")}`} className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/30 p-3 hover:-translate-y-0.5 transition-transform" style={{ borderColor: `${s.c}2e` }}>
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${s.c}1f`, border: `1px solid ${s.c}55` }}>
                    <s.Icon className="h-4.5 w-4.5" style={{ color: s.c }} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-base text-white tracking-[0.03em] flex items-center gap-2"><span className="font-mono text-[10px] text-white/30">{i + 1}</span> {s.t}</div>
                    <div className="text-[12px] text-white/55 leading-snug mt-0.5">{s.d}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="hud-pill rounded-2xl p-6 h-full relative overflow-hidden flex flex-col">
            <div className="absolute -top-16 -right-12 w-60 h-60 rounded-full bg-rose-500/10 blur-[80px] pointer-events-none" />
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-rose-300/80"><Swords className="h-3.5 w-3.5" /> 225-Move Arsenal</div>
            <div className="grid grid-cols-2 gap-2.5 mt-4">
              {MOVE_KINDS.map((m) => (
                <div key={m.t} className="rounded-xl border border-white/10 bg-black/30 p-3.5" style={{ borderColor: `${m.c}2e` }}>
                  <m.Icon className="h-5 w-5" style={{ color: m.c }} />
                  <div className="font-display text-base text-white mt-2 tracking-[0.03em]">{m.t}</div>
                  <div className="text-[11px] text-white/55 leading-snug mt-0.5">{m.d}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-xl border border-emerald-400/25 bg-emerald-400/[0.06] p-3 flex items-center gap-3">
              <HeartPulse className="h-5 w-5 text-emerald-300 shrink-0" />
              <div className="text-[11px] text-white/65 leading-snug"><span className="text-white font-medium">Hover-pick your heal target.</span> Save a wounded ally in the Arena squad or your Cavern party — any heal also burns away poison.</div>
            </div>
            <div className="mt-auto pt-4 flex flex-wrap gap-2">
              <Button asChild size="sm" data-testid="arsenal-arena-cta" className="bg-rose-400 text-black hover:bg-rose-300"><Link to="/arena"><Swords className="mr-1.5 h-3.5 w-3.5" /> VS Arena</Link></Button>
              <Button asChild size="sm" variant="outline" data-testid="arsenal-cavern-cta" className="border-fuchsia-400/40 text-fuchsia-200 hover:bg-fuchsia-400/10"><Link to="/cavern"><Mountain className="mr-1.5 h-3.5 w-3.5" /> Cavern Raids</Link></Button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ── HOW CARDS ARE FOUND + LIVE VITALS CHART ─────────────────────── */
function CollectionVitals() {
  const { data: stats } = useHomeStats();
  const { data: featuredPack } = useQuery({ queryKey: ["home-featured-pack"], queryFn: async () => (await api.get("/home/featured-pack")).data });
  const undiscovered = stats?.undiscovered ?? 0;
  const minted = stats?.minted ?? 0;
  const totalSupply = stats?.total_supply ?? 0;
  const mintedPct = totalSupply > 0 ? Math.round((minted / totalSupply) * 100) : 0;
  const byRarity = stats?.by_rarity || {};
  const breakdown = stats?.rarity_breakdown || RARITY_BAR.map((r) => ({ rarity: r.key, minted: byRarity[r.key] || 0, unminted: 0 }));
  const maxBar = Math.max(1, ...breakdown.flatMap((b) => [b.minted, b.unminted]));

  const ways = [
    { icon: Package, testid: "packs", title: "Card Packs", desc: "Rip a pack and roll random cards against published rarity odds. Cheaper per card, riskier.", to: "/store", color: "#a78bfa" },
    { icon: Gift, testid: "codes", title: "Claim Codes", desc: "Redeem a drop or event code to unlock a specific card or earn more credits.", to: "/redeem", color: "#fbbf24" },
    { icon: Flag, testid: "gauntlet", title: "Gauntlet Wins", desc: "Win gear or consumables. Credit winnings come only from human-funded wagers; AI never adds to the pot.", to: "/race", color: "#34d399" },
    { icon: Mountain, testid: "cavern", title: "Cavern Loot", desc: "Survive a winning raid: 25% card chance, otherwise credits and a Reroll Token, plus gear and depth loot.", to: "/cavern", color: "#e879f9" },
    { icon: ChevronsUp, testid: "ascension", title: "Ascension Forge", desc: "Sacrifice 10 cards of one rarity to forge a brand-new card one full tier higher.", to: "/ascend", color: "#f472b6" },
    { icon: Send, testid: "requests", title: "Card Requests", desc: "Pitch a card you want made — once an admin approves it, a fresh copy is minted to you.", to: "/requests", color: "#c084fc" },
  ];

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="home-collection-vitals">
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8 items-center">
        <Reveal>
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-emerald-300/80">Playden Is Vast</div>
          <h2 className="font-display text-4xl sm:text-5xl text-white tracking-[0.06em] mt-2 leading-tight">STILL OUT THERE<br />TO BE FOUND.</h2>
          <div className="mt-6 hud-pill rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute inset-0 holo-scan opacity-20" />
            <div className="font-display text-6xl sm:text-7xl bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-transparent" data-testid="vitals-undiscovered">
              {fmtCompact(undiscovered)}+
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 mt-2">Cards still undiscovered</div>
            <div className="mt-4">
              <div className="h-2 rounded-full bg-black/60 border border-white/10 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400" style={{ width: `${mintedPct}%` }} />
              </div>
              <div className="font-mono text-[10px] text-white/40 mt-1">{mintedPct}% of all editions minted · {fmtCompact(minted)} of {fmtCompact(totalSupply)}</div>
            </div>
          </div>
          <div className="hud-pill rounded-xl p-4 mt-3" data-testid="vitals-rarity-chart">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">Minted vs unminted by rarity</div>
              <div className="flex items-center gap-3 font-mono text-[8px] uppercase tracking-wider">
                <span className="inline-flex items-center gap-1 text-white/55"><span className="h-2 w-2 rounded-sm bg-cyan-400" /> Minted</span>
                <span className="inline-flex items-center gap-1 text-white/40"><span className="h-2 w-2 rounded-sm bg-white/25" /> Unminted</span>
              </div>
            </div>
            <div className="flex items-end gap-2 sm:gap-3 h-28">
              {breakdown.map((b) => {
                const color = RARITY_COLORS[b.rarity] || "#9ca3af";
                return (
                  <div key={b.rarity} className="flex-1 flex flex-col items-center justify-end gap-1" data-testid={`rarity-bar-${b.rarity}`}>
                    <div className="flex items-end gap-1 w-full justify-center h-full">
                      <div className="w-[45%] rounded-t-sm transition-all duration-500" style={{ height: `${Math.max(3, (b.minted / maxBar) * 100)}%`, backgroundColor: color }} title={`${b.minted} minted`} />
                      <div className="w-[45%] rounded-t-sm transition-all duration-500" style={{ height: `${Math.max(3, (b.unminted / maxBar) * 100)}%`, backgroundColor: color, opacity: 0.28 }} title={`${b.unminted} unminted`} />
                    </div>
                    <div className="font-mono text-[13px] sm:text-base font-semibold leading-none mt-0.5 whitespace-nowrap" data-testid={`rarity-count-${b.rarity}`}>
                      <span style={{ color }}>{fmtCompact(b.minted)}</span>
                      <span className="text-white/30 mx-0.5">/</span>
                      <span className="text-white/55">{fmtCompact(b.unminted)}</span>
                    </div>
                    <div className="font-mono text-[8px] sm:text-[9px] uppercase tracking-wider text-white/40 truncate">{b.rarity.slice(0, 4)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {featuredPack && featuredPack.active && (
            <Link to="/store" data-testid="featured-pack-quicklink" className="mt-3 block hud-pill rounded-2xl p-4 relative overflow-hidden group hover:border-amber-400/40 transition-colors">
              <div className="absolute inset-0 holo-scan opacity-10" />
              <div className="absolute top-3 right-3 flex items-center gap-1 text-amber-300">
                <Star className="h-4 w-4 fill-amber-300" />
                <span className="font-mono text-[9px] uppercase tracking-[0.2em]">Featured Pack</span>
              </div>
              <div className="flex items-center gap-4 relative">
                <div className="h-16 w-16 rounded-xl overflow-hidden border border-amber-400/40 shrink-0 bg-black/40 grid place-items-center" style={{ boxShadow: "0 0 22px rgba(251,191,36,0.3)" }}>
                  {featuredPack.image ? <img src={featuredPack.image} alt={featuredPack.name} className="w-full h-full object-cover" /> : <Package className="h-7 w-7 text-amber-300" />}
                </div>
                <div className="min-w-0">
                  <div className="font-display text-xl text-white tracking-[0.04em] truncate">{featuredPack.name}</div>
                  <div className="text-[11px] text-white/55 mt-0.5 truncate">{featuredPack.description || "Best odds to discover something new."}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    {featuredPack.price_credits != null && <span className="inline-flex items-center gap-1 font-mono text-xs text-amber-300"><Coins className="h-3.5 w-3.5" /> {featuredPack.price_credits.toLocaleString()}</span>}
                    <span className="text-[10px] text-white/40">· Rip in the store</span>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-white/20 group-hover:text-amber-300 transition-colors ml-auto self-center shrink-0" />
              </div>
            </Link>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80">How to Find Them</div>
          <h3 className="font-display text-3xl sm:text-4xl text-white tracking-[0.06em] mt-2">SIX WAYS TO MINT.</h3>
          <p className="text-white/55 mt-3 text-sm leading-relaxed">Every fresh mint rolls its own unique Find-on-Mint traits — so even two copies of the same design fight differently.</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            {ways.map((w) => (
              <Link key={w.title} to={w.to} data-testid={`find-way-${w.testid}`} className="hud-pill rounded-2xl p-4 flex items-start gap-4 hover:border-white/25 transition-colors group block">
                <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${w.color}22`, border: `1px solid ${w.color}55` }}>
                  <w.icon className="h-5 w-5" style={{ color: w.color }} />
                </div>
                <div className="min-w-0">
                  <div className="font-display text-lg text-white tracking-[0.04em]">{w.title}</div>
                  <div className="text-[13px] text-white/55 mt-0.5 leading-relaxed">{w.desc}</div>
                </div>
              </Link>
            ))}
          </div>
          <Button asChild data-testid="vitals-explore-cta" variant="outline" className="border-white/15 text-white hover:bg-white/5 mt-4">
            <Link to="/explore"><Telescope className="mr-2 h-4 w-4" /> Browse the full catalog</Link>
          </Button>
        </Reveal>
      </div>
    </section>
  );
}

/* ── THE FORGES ───────────────────────────────────────────────────── */
function ForgesShowcase() {
  const forges = [
    { icon: ChevronsUp, color: "#f472b6", title: "ASCENSION FORGE", testid: "forge-ascension-card", to: "/ascend", desc: "Sacrifice 10 cards of one rarity to mint a single card of the next tier up. Trade quantity for prestige." },
    { icon: Wand2, color: "#c084fc", title: "UPGRADE", testid: "forge-waifu-card", to: "/upgrade", desc: "Gamble credits to reroll a card's stats, ability, traits or attacks — with up/down feedback on every roll. Hold Reroll Tokens? Spend one instead of credits." },
    { icon: ImageIcon, color: "#22d3ee", title: "HQ ENHANCEMENTS", testid: "forge-hq-card", to: "/requests", desc: "Request a higher-quality redraw of your card. Chat with the studio and pick from multiple versions." },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="home-forges">
      <Reveal>
        <SectionHeading eyebrow="Reforge & Upgrade" title="YOUR CARDS NEVER STOP EVOLVING." accent="#c084fc">
          Unhappy with a roll, or want to climb tiers? Three forges let you reshape, upgrade and beautify your collection.
        </SectionHeading>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-3">
        {forges.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.08}>
            <Link to={f.to} data-testid={f.testid} className="hud-pill rounded-2xl p-6 relative overflow-hidden group hover:border-white/25 hover:-translate-y-1 transition-all block h-full">
              <div className="absolute inset-0 holo-scan opacity-15" />
              <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${f.color}22`, border: `1px solid ${f.color}55` }}>
                <f.icon className="h-6 w-6" style={{ color: f.color }} />
              </div>
              <div className="font-display text-xl text-white mt-4 tracking-[0.06em]">{f.title}</div>
              <div className="text-sm text-white/55 mt-2 leading-relaxed">{f.desc}</div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ── ECONOMY & SOCIAL ─────────────────────────────────────────────── */
const ECONOMY = [
  { icon: Bitcoin, color: "#fbbf24", title: "Crypto Top-Ups", to: "/store", testid: "economy-crypto", desc: "Buy credits with crypto via NOWPayments — pay in BTC, ETH, USDT and more. Credits land when payment confirms." },
  { icon: StoreIcon, color: "#22d3ee", title: "Store & Packs", to: "/store", testid: "economy-store-link", desc: "Buy the exact card you want, or rip a pack and gamble on published rarity odds." },
  { icon: Gift, color: "#34d399", title: "Redeem Codes", to: "/redeem", testid: "economy-redeem", desc: "Enter claim, drop and event codes to unlock specific cards or top up credits." },
  { icon: Shuffle, color: "#a78bfa", title: "Trading Board", to: "/requests", testid: "economy-trades", desc: "Post what you want back, stake a bounty, and let offers accumulate on the board. Everything (cards, tokens, gear, mats) trades safely through consent-gated request/offer flow." },
  { icon: Backpack, color: "#f59e0b", title: "Gear Market", to: "/store", testid: "economy-gear-market", desc: "Sell crafted gear to other collectors or buy theirs, and crack admin Gear Crates for a random piece — a full loot economy." },
  { icon: Lightbulb, color: "#facc15", title: "Player Request Board", to: "/requests", testid: "economy-requests", desc: "Post a 'Wanted' card, pitch new designs, or commission an HQ redraw — a true P2P board." },
  { icon: MessageSquare, color: "#22d3ee", title: "Lobby Chat", to: "/collection", testid: "economy-chat", desc: "Live tabbed chat — General, Trade & Bet. Share cards, offer trades, and see a 'Bettable Now' panel of live matches." },
  { icon: User, color: "#f472b6", title: "Profile & Alerts", to: "/profile", testid: "economy-profile", desc: "Track your rarity tally and crypto purchase history, catch every call-out via notifications and the live award broadcast." },
];

function EconomySocial() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="home-economy">
      <Reveal>
        <SectionHeading eyebrow="Economy & Social" title="ONE CREDIT, A WHOLE WORLD." accent="#22d3ee">
          Top up once and everything opens up — buying, trading, Challenge Entries, Community Prediction Pools, requests and chat all run on the same balance.
        </SectionHeading>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ECONOMY.map((f, i) => (
          <Reveal key={f.title} delay={(i % 3) * 0.07}>
            <Link to={f.to} data-testid={f.testid} className="hud-pill rounded-2xl p-5 flex items-start gap-4 hover:border-white/25 transition-colors group h-full">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${f.color}22`, border: `1px solid ${f.color}55` }}>
                <f.icon className="h-5 w-5" style={{ color: f.color }} />
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg text-white tracking-[0.04em] flex items-center gap-2">{f.title}<ArrowRight className="h-3.5 w-3.5 text-white/0 group-hover:text-white/50 transition-colors" /></div>
                <div className="text-[13px] text-white/55 mt-1 leading-relaxed">{f.desc}</div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ── LIVE FEED ────────────────────────────────────────────────────── */
function LatestDrops() {
  const navigate = useNavigate();
  const { data: explore } = useQuery({ queryKey: ["explore-home"], queryFn: async () => (await api.get("/cards/explore?limit=10")).data });
  const cards = explore?.items || [];
  if (cards.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="explore-catalog-feed">
      <Reveal>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80">Live Feed</div>
            <h2 className="font-display text-4xl text-white tracking-[0.06em] mt-2">LATEST MINTS</h2>
          </div>
          <Link to="/explore" className="text-cyan-300 hover:text-cyan-200 font-mono text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2" data-testid="latest-explore-all">
            Explore All <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </Reveal>
      <div className={TINY_GRID}>
        {cards.slice(0, 10).map((c) => (
          <TinyCard key={c.id} card={c} count={c.stack_count || 1} idKey={c.template_id || c.id} onClick={() => navigate(`/card/${c.id}`)} />
        ))}
      </div>
    </section>
  );
}

/* ── FINAL CTA ────────────────────────────────────────────────────── */
function FinalCTA() {
  const { user } = useAuth();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <Reveal>
        <div className="hud-pill rounded-3xl p-8 sm:p-12 relative overflow-hidden text-center">
          <div className="absolute inset-0 holo-scan opacity-15 pointer-events-none" />
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[30rem] h-[30rem] rounded-full bg-cyan-500/10 blur-[120px] pointer-events-none" />
          <div className="relative">
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cyan-300/80">Ready Player</div>
            <h2 className="font-display text-4xl sm:text-6xl text-white tracking-[0.05em] mt-3">COLLECT. BATTLE. <span className="bg-gradient-to-r from-cyan-300 to-amber-300 bg-clip-text text-transparent">PLAY FOR KEEPS.</span></h2>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              {user ? (
                <>
                  <Button asChild size="lg" className="bg-cyan-400 text-black hover:bg-cyan-300"><Link to="/store"><StoreIcon className="mr-2 h-4 w-4" /> Top Up & Shop</Link></Button>
                  <Button asChild size="lg" variant="outline" className="border-white/15 text-white hover:bg-white/5"><Link to="/cavern"><Mountain className="mr-2 h-4 w-4" /> Raid the Cavern</Link></Button>
                </>
              ) : (
                <>
                  <Button asChild data-testid="final-cta-join" size="lg" className="bg-cyan-400 text-black hover:bg-cyan-300"><Link to="/register">Join Free <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
                  <Button asChild size="lg" variant="outline" className="border-white/15 text-white hover:bg-white/5"><Link to="/explore"><Telescope className="mr-2 h-4 w-4" /> Explore the Catalog</Link></Button>
                </>
              )}
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ── COMPLIANCE ───────────────────────────────────────────────────── */
function ComplianceNotice() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16" data-testid="home-compliance-notice">
      <div className="hud-pill rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row items-start gap-5 relative overflow-hidden">
        <div className="absolute inset-0 holo-scan opacity-10 pointer-events-none" />
        <div className="h-12 w-12 rounded-xl bg-emerald-400/15 border border-emerald-300/30 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-6 w-6 text-emerald-300" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-emerald-300/80">Content &amp; Compliance</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-black bg-emerald-300 rounded-full px-2 py-0.5">21+ Only</span>
          </div>
          <h3 className="font-display text-2xl sm:text-3xl text-white tracking-[0.05em] mt-2">EVERYONE'S AN ADULT. EVERYTHING'S CONSENSUAL.</h3>
          <p className="text-white/60 text-sm leading-relaxed mt-2 max-w-3xl">
            All persons and cards depicted on Playden are <span className="text-white">21 years of age or older</span>, and every depiction portrays
            <span className="text-white"> consensual activity between consenting adults</span>. All content is produced and presented in compliance with
            applicable laws. Nothing here depicts minors, non-consent, or any unlawful conduct — and any content that would violate the law is strictly prohibited.
          </p>
          <p className="text-white/35 text-[11px] leading-relaxed mt-3 max-w-3xl">
            By browsing, you confirm you are of legal age in your jurisdiction. Records of age and consent are maintained where required by law.
          </p>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div>
      <Hero />
      <RaritySpectrum />
      <div className="neon-divider max-w-7xl mx-auto" />
      <MintingEngine />
      <div className="neon-divider max-w-7xl mx-auto" />
      <GameModesBento />
      <div className="neon-divider max-w-7xl mx-auto" />
      <PathPrestige />
      <div className="neon-divider max-w-7xl mx-auto" />
      <LevelingEvolution />
      <div className="neon-divider max-w-7xl mx-auto" />
      <ArsenalAndAdventure />
      <div className="neon-divider max-w-7xl mx-auto" />
      <CollectionVitals />
      <div className="neon-divider max-w-7xl mx-auto" />
      <ForgesShowcase />
      <div className="neon-divider max-w-7xl mx-auto" />
      <EconomySocial />
      <div className="neon-divider max-w-7xl mx-auto" />
      <LatestDrops />
      <FinalCTA />
      <ComplianceNotice />
    </div>
  );
}
